import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  Chip,
  Divider,
  Switch,
  FormControlLabel,
  CircularProgress,
  alpha,
  useTheme,
  Container
} from '@mui/material';
import {
  Delete as DeleteIcon,
  Block as DeactivateIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  AdminPanelSettings as AdminIcon
} from '@mui/icons-material';
import { supabase } from '../../../supabaseClient';
import { usePermissions } from '../../../utils/permissions';

export default function DeleteCompany() {
  const theme = useTheme();
  const { hierarchyLevel, companyId, loading: permissionsLoading, refresh: refreshPermissions } = usePermissions();
  const [company, setCompany] = useState(null);
  const [loading, setLoading] = useState(true);
  const [deactivateDialogOpen, setDeactivateDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteConfirmStep, setDeleteConfirmStep] = useState(1); // 1, 2, 3
  const [confirmationText, setConfirmationText] = useState('');
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    loadCompanyData();
  }, [companyId]);

  const loadCompanyData = async () => {
    try {
      if (!companyId) return;

      const { data: companyData, error } = await supabase
        .from('companies')
        .select('*')
        .eq('id', companyId)
        .single();

      if (error) throw error;
      setCompany(companyData);
    } catch (err) {
      console.error('Error loading company:', err);
      setError('Failed to load company information');
    } finally {
      setLoading(false);
    }
  };

  const handleDeactivateCompany = async () => {
    if (confirmationText !== company?.name) {
      setError('Company name does not match');
      return;
    }

    setProcessing(true);
    setError('');

    try {
      const { error } = await supabase
        .from('companies')
        .update({ 
          is_active: false,
          deactivated_at: new Date().toISOString(),
          deactivated_by: (await supabase.auth.getUser()).data.user.id
        })
        .eq('id', company.id);

      if (error) throw error;

      setSuccess('Company deactivated successfully. Redirecting...');
      setDeactivateDialogOpen(false);
      setConfirmationText('');
      
      // Refresh permissions to trigger redirect to CompanyDisabled page
      await refreshPermissions();
      
      // If permissions don't trigger redirect automatically, force reload
      setTimeout(() => {
        window.location.reload();
      }, 2000);
    } catch (err) {
      console.error('Error deactivating company:', err);
      setError('Failed to deactivate company: ' + err.message);
    } finally {
      setProcessing(false);
    }
  };

  const handleReactivateCompany = async () => {
    setProcessing(true);
    setError('');

    try {
      const { error } = await supabase
        .from('companies')
        .update({ 
          is_active: true,
          deactivated_at: null,
          deactivated_by: null
        })
        .eq('id', company.id);

      if (error) throw error;

      setSuccess('Company reactivated successfully. Users can now access the system.');
      await loadCompanyData();
    } catch (err) {
      console.error('Error reactivating company:', err);
      setError('Failed to reactivate company: ' + err.message);
    } finally {
      setProcessing(false);
    }
  };

  const handleDeleteConfirmStep = () => {
    if (deleteConfirmStep === 1) {
      setDeleteConfirmStep(2);
    } else if (deleteConfirmStep === 2) {
      setDeleteConfirmStep(3);
    } else if (deleteConfirmStep === 3) {
      handleFinalDelete();
    }
  };

  const handleFinalDelete = async () => {
    if (confirmationText !== company?.name) {
      setError('Company name does not match. Please type the exact company name: ' + company?.name);
      return;
    }

    setProcessing(true);
    setError('');

    try {
      // Call the comprehensive delete function
      const { error } = await supabase.rpc('delete_company_completely', {
        p_company_id: company.id
      });

      if (error) throw error;

      setSuccess('Company deleted permanently. Redirecting...');
      setDeleteDialogOpen(false);
      
      // Sign out user and redirect after deletion
      setTimeout(async () => {
        await supabase.auth.signOut();
        window.location.href = '/';
      }, 3000);
    } catch (err) {
      console.error('Error deleting company:', err);
      setError('Failed to delete company: ' + err.message);
    } finally {
      setProcessing(false);
    }
  };

  const handleDeleteDialogClose = () => {
    if (!processing) {
      setDeleteDialogOpen(false);
      setDeleteConfirmStep(1);
      setConfirmationText('');
      setError('');
    }
  };

  // Wait for permissions to load before showing anything
  if (permissionsLoading) {
    return (
      <Card sx={{ 
        mb: 4,
        borderRadius: 3,
        border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
        background: `linear-gradient(135deg, ${alpha(theme.palette.grey[50], 0.9)}, ${alpha(theme.palette.grey[100], 0.9)})`,
        boxShadow: `0 12px 32px ${alpha(theme.palette.primary.main, 0.15)}`,
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow: `0 20px 40px ${alpha(theme.palette.primary.main, 0.25)}`,
          border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`
        }
      }}>
        <CardContent sx={{ p: 4 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
            <Box sx={{
              p: 2,
              borderRadius: 2,
              background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.1)}, ${alpha(theme.palette.primary.main, 0.05)})`,
              border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`
            }}>
              <CircularProgress size={24} sx={{ color: 'primary.main' }} />
            </Box>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 500, mb: 0.5, color: 'primary.main' }}>
                Loading Permissions
              </Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                Verifying your access level and loading company controls...
              </Typography>
            </Box>
          </Box>
        </CardContent>
      </Card>
    );
  }

  // Only Super Admins can access this
  if (hierarchyLevel !== 1) {
    return (
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Box sx={{ 
          display: 'flex', 
          flexDirection: 'column', 
          alignItems: 'center', 
          justifyContent: 'center',
          minHeight: '50vh',
          textAlign: 'center'
        }}>
          <Card sx={{
            p: 6,
            borderRadius: 4,
            border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
            background: 'white',
            boxShadow: `0 12px 40px ${alpha(theme.palette.grey[900], 0.08)}`,
            maxWidth: 500,
            width: '100%',
            position: 'relative',
            overflow: 'hidden',
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            '&:hover': {
              transform: 'translateY(-2px)',
              boxShadow: `0 16px 48px ${alpha(theme.palette.primary.main, 0.15)}`,
              border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`
            },
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.03)}, transparent)`,
              zIndex: 0,
              opacity: 0.8,
              transition: 'opacity 0.3s ease'
            },
            '&:hover::before': {
              opacity: 1
            }
          }}>
            <Box sx={{ 
              p: 3, 
              borderRadius: 3, 
              background: `linear-gradient(${alpha(theme.palette.primary.main, 0.05)}, ${alpha(theme.palette.primary.main, 0.02)})`,
              border: `1px solid ${alpha(theme.palette.primary.main, 0.15)}`,
              mb: 6,
              display: 'inline-flex',
              boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.15)}`
            }}>
              <AdminIcon sx={{ fontSize: 48, color: 'text.secondary' }} />
            </Box>
            <Typography variant="h3" sx={{ 
              mb: 3, 
              fontWeight: 500, 
              color: 'text.primary',
              fontSize: '2.5rem',
              letterSpacing: '-0.8px'
            }}>
              Access Restricted
            </Typography>
            <Typography variant="h6" sx={{ 
              color: 'text.secondary', 
              mb: 4,
              fontWeight: 500,
              fontSize: '1.2rem'
            }}>
              Company Control Required
            </Typography>
            <Typography variant="body1" sx={{ 
              color: 'text.secondary',
              lineHeight: 1.7,
              mb: 6,
              fontSize: '1.05rem',
              fontWeight: 500
            }}>
              You don't currently have the necessary permissions to access Company Control features. Only Super Administrators can manage company status, deactivation, and deletion.
            </Typography>
          </Card>
        </Box>
      </Container>
    );
  }

  if (loading || !company) {
    return (
      <Card sx={{ 
        mb: 4,
        borderRadius: 3,
        border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
        background: `linear-gradient(135deg, ${alpha(theme.palette.grey[50], 0.9)}, ${alpha(theme.palette.grey[100], 0.9)})`,
        boxShadow: `0 12px 32px ${alpha(theme.palette.primary.main, 0.15)}`,
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow: `0 20px 40px ${alpha(theme.palette.primary.main, 0.25)}`,
          border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`
        }
      }}>
        <CardContent sx={{ p: 4 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
            <Box sx={{
              p: 2,
              borderRadius: 2,
              background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.1)}, ${alpha(theme.palette.primary.main, 0.05)})`,
              border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`
            }}>
              <CircularProgress size={24} sx={{ color: 'primary.main' }} />
            </Box>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 500, mb: 0.5, color: 'primary.main' }}>
                Loading Company Information
              </Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                {loading ? 'Fetching company details and settings...' : 'Unable to load company information.'}
              </Typography>
            </Box>
          </Box>
        </CardContent>
      </Card>
    );
  }

  return (
    <Box>
      {/* Status Messages */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      {/* Company Status Card */}
      <Card sx={{ 
        mb: 4,
        borderRadius: 3,
        border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
        background: `linear-gradient(135deg, ${alpha(theme.palette.grey[50], 0.9)}, ${alpha(theme.palette.grey[100], 0.9)})`,
        boxShadow: `0 12px 32px ${alpha(theme.palette.primary.main, 0.15)}`,
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow: `0 20px 40px ${alpha(theme.palette.primary.main, 0.25)}`,
          border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`
        }
      }}>
        <CardContent sx={{ p: 4 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="h5" sx={{ 
              fontWeight: 500,
              fontSize: '1.3rem',
              color: '#1f2937'
            }}>
              Company Status
            </Typography>
            <Chip 
              label={company?.is_active !== false ? 'Active' : 'Deactivated'}
              color={company?.is_active !== false ? 'success' : 'error'}
              icon={company?.is_active !== false ? <CheckCircleIcon /> : <DeactivateIcon />}
              sx={{
                fontWeight: 500,
                fontSize: '0.85rem',
                height: 32
              }}
            />
          </Box>
          
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 500, color: '#374151' }}>
            {company?.name}
          </Typography>
          
          {company?.is_active === false && company?.deactivated_at && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2, fontStyle: 'italic' }}>
              Deactivated on {new Date(company.deactivated_at).toLocaleDateString()}
            </Typography>
          )}

          <Typography variant="body2" color="text.secondary" sx={{ mb: 3, lineHeight: 1.6 }}>
            {company?.is_active !== false
              ? 'Company is currently active. All users can access the system normally.'
              : 'Company is deactivated. All users are blocked from accessing the system except for reactivation.'
            }
          </Typography>

          {company?.is_active !== false ? (
            <Button
              variant="outlined"
              color="warning"
              startIcon={<DeactivateIcon />}
              onClick={() => setDeactivateDialogOpen(true)}
              disabled={processing}
              sx={{
                borderRadius: 3,
                px: 3,
                py: 1.5,
                fontWeight: 500,
                textTransform: 'none',
                border: '1px solid #f59e0b',
                color: '#d97706',
                '&:hover': {
                  border: '1px solid #d97706',
                  backgroundColor: 'rgba(245, 158, 11, 0.1)',
                  transform: 'translateY(-1px)',
                  boxShadow: '0 4px 16px rgba(245, 158, 11, 0.3)'
                }
              }}
            >
              Deactivate Company
            </Button>
          ) : (
            <Button
              variant="contained"
              color="success"
              startIcon={<CheckCircleIcon />}
              onClick={handleReactivateCompany}
              disabled={processing}
              sx={{
                borderRadius: 3,
                px: 3,
                py: 1.5,
                fontWeight: 500,
                textTransform: 'none',
                backgroundColor: '#10b981',
                '&:hover': {
                  backgroundColor: '#059669',
                  transform: 'translateY(-1px)',
                  boxShadow: '0 4px 16px rgba(16, 185, 129, 0.4)'
                }
              }}
            >
              {processing ? 'Reactivating...' : 'Reactivate Company'}
            </Button>
          )}
        </CardContent>
      </Card>

      {/* Danger Zone */}
      <Card sx={{ 
        borderRadius: 3,
        border: '1px solid #ef4444',
        background: 'linear-gradient(135deg, rgba(239, 68, 68, 0.05), rgba(248, 113, 113, 0.02))',
        boxShadow: '0 8px 32px rgba(239, 68, 68, 0.15)',
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow: '0 12px 48px rgba(239, 68, 68, 0.25)',
          border: '1px solid #dc2626'
        }
      }}>
        <CardContent sx={{ p: 4 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
            <Box sx={{
              p: 1.5,
              borderRadius: 2,
              backgroundColor: 'rgba(239, 68, 68, 0.1)',
              border: '1px solid rgba(239, 68, 68, 0.3)'
            }}>
              <WarningIcon sx={{ color: '#ef4444', fontSize: 24 }} />
            </Box>
            <Typography variant="h5" sx={{ 
              fontWeight: 500,
              color: '#ef4444',
              fontSize: '1.3rem'
            }}>
              Danger Zone
            </Typography>
          </Box>
          
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 500, color: '#374151' }}>
            Permanently Delete Company
          </Typography>
          
          <Typography variant="body2" color="text.secondary" sx={{ mb: 4, lineHeight: 1.6 }}>
            This action will <strong>permanently delete</strong> the company and <strong>ALL associated data</strong> including:
            products, formulations, inventory, batches, customers, invoices, users, and every other record.
            <br/><br/>
            <strong>This action cannot be undone.</strong>
          </Typography>

          <Button
            variant="contained"
            color="error"
            startIcon={<DeleteIcon />}
            onClick={() => setDeleteDialogOpen(true)}
            disabled={processing}
            sx={{
              borderRadius: 3,
              px: 3,
              py: 1.5,
              fontWeight: 500,
              textTransform: 'none',
              backgroundColor: '#ef4444',
              '&:hover': {
                backgroundColor: '#dc2626',
                transform: 'translateY(-1px)',
                boxShadow: '0 4px 16px rgba(239, 68, 68, 0.4)'
              }
            }}
          >
            Delete Company Permanently
          </Button>
        </CardContent>
      </Card>

      {/* Deactivate Confirmation Dialog */}
      <Dialog 
        open={deactivateDialogOpen} 
        onClose={() => !processing && setDeactivateDialogOpen(false)}
        maxWidth="sm" 
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
            boxShadow: `0 20px 60px ${alpha(theme.palette.primary.main, 0.15)}`,
            background: `linear-gradient(135deg, ${alpha(theme.palette.grey[50], 0.9)}, ${alpha(theme.palette.grey[100], 0.9)})`,
            backdropFilter: 'blur(20px)'
          }
        }}
      >
        <DialogTitle sx={{ 
          color: 'warning.main', 
          fontWeight: 500,
          fontSize: '1.5rem',
          borderBottom: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
          pb: 3,
          mb: 2,
          background: `linear-gradient(135deg, ${alpha(theme.palette.warning.main, 0.08)}, ${alpha(theme.palette.warning.main, 0.04)})`
        }}>
          Deactivate Company
        </DialogTitle>
        <DialogContent sx={{ pt: 0, pb: 4 }}>
          <Typography sx={{ mb: 3, fontSize: '1.1rem', fontWeight: 500 }}>
            Are you sure you want to deactivate <strong>{company?.name}</strong>?
          </Typography>
          
          <Box sx={{
            p: 3,
            borderRadius: 2,
            background: alpha(theme.palette.warning.main, 0.05),
            border: `1px solid ${alpha(theme.palette.warning.main, 0.2)}`,
            mb: 3
          }}>
            <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.6 }}>
              This will immediately block all users from accessing the system. You can reactivate 
              the company later to restore access.
            </Typography>
          </Box>

          <TextField
            fullWidth
            label={`Type company name "${company?.name}" to confirm`}
            value={confirmationText}
            onChange={(e) => setConfirmationText(e.target.value)}
            sx={{ 
              '& .MuiInputLabel-root': { fontWeight: 500 },
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
                background: alpha(theme.palette.grey[50], 0.8),
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: alpha(theme.palette.primary.main, 0.5)
                },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'primary.main',
                  borderWidth: '2px'
                }
              }
            }}
          />
        </DialogContent>
        <DialogActions sx={{ 
          p: 3, 
          borderTop: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
          background: alpha(theme.palette.grey[50], 0.5),
          gap: 2
        }}>
          <Button 
            onClick={() => {
              setDeactivateDialogOpen(false);
              setConfirmationText('');
              setError('');
            }}
            disabled={processing}
            sx={{ 
              fontWeight: 500,
              borderRadius: 2,
              px: 3,
              py: 1.2,
              color: 'text.secondary',
              '&:hover': {
                background: alpha(theme.palette.grey[400], 0.1)
              }
            }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleDeactivateCompany}
            color="warning"
            variant="contained"
            disabled={processing || confirmationText !== company?.name}
            startIcon={processing ? <CircularProgress size={16} /> : <DeactivateIcon />}
            sx={{
              fontWeight: 500,
              borderRadius: 2,
              px: 4,
              py: 1.2,
              boxShadow: `0 4px 16px ${alpha(theme.palette.warning.main, 0.3)}`,
              '&:hover': {
                transform: 'translateY(-1px)',
                boxShadow: `0 6px 20px ${alpha(theme.palette.warning.main, 0.4)}`
              }
            }}
          >
            {processing ? 'Deactivating...' : 'Deactivate Company'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog - Multi-Step */}
      <Dialog 
        open={deleteDialogOpen} 
        onClose={handleDeleteDialogClose}
        maxWidth="sm" 
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
            boxShadow: `0 20px 60px ${alpha(theme.palette.primary.main, 0.15)}`,
            background: `linear-gradient(135deg, ${alpha(theme.palette.grey[50], 0.9)}, ${alpha(theme.palette.grey[100], 0.9)})`,
            backdropFilter: 'blur(20px)'
          }
        }}
      >
        <DialogTitle sx={{ 
          color: 'error.main', 
          fontWeight: 500,
          fontSize: '1.5rem',
          borderBottom: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
          pb: 3,
          mb: 2,
          background: `linear-gradient(135deg, ${alpha(theme.palette.error.main, 0.08)}, ${alpha(theme.palette.error.main, 0.04)})`
        }}>
          {deleteConfirmStep === 1 && 'Delete Company'}
          {deleteConfirmStep === 2 && 'I want to delete'}
          {deleteConfirmStep === 3 && 'There is no going back'}
        </DialogTitle>
        <DialogContent sx={{ pt: 0, pb: 4 }}>
          {deleteConfirmStep === 1 && (
            <>
              <Typography sx={{ mb: 3, fontSize: '1.1rem', fontWeight: 500 }}>
                Are you sure you want to delete <strong>{company?.name}</strong>?
              </Typography>
              <Box sx={{
                p: 3,
                borderRadius: 2,
                background: alpha(theme.palette.error.main, 0.05),
                border: `1px solid ${alpha(theme.palette.error.main, 0.2)}`
              }}>
                <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.6 }}>
                  This will permanently remove the company and all its data.
                </Typography>
              </Box>
            </>
          )}

          {deleteConfirmStep === 2 && (
            <>
              <Alert 
                severity="warning" 
                sx={{ 
                  mb: 3,
                  borderRadius: 2,
                  border: `1px solid ${alpha(theme.palette.warning.main, 0.3)}`,
                  background: alpha(theme.palette.warning.main, 0.05)
                }}
              >
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  This action will delete everything!
                </Typography>
              </Alert>
              <Typography sx={{ mb: 3, fontSize: '1.1rem', fontWeight: 500 }}>
                You are about to permanently delete <strong>{company?.name}</strong> and ALL associated data including:
              </Typography>
              <Box sx={{
                p: 3,
                borderRadius: 2,
                background: alpha(theme.palette.error.main, 0.05),
                border: `1px solid ${alpha(theme.palette.error.main, 0.2)}`,
                mb: 3
              }}>
                <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.8 }}>
                  • All products, formulations, and inventory records<br/>
                  • All production batches and quality control data<br/>
                  • All customers, invoices, and sales data<br/>
                  • All users and their access to the system<br/>
                  • Every other piece of data in this company
                </Typography>
              </Box>
              <Typography variant="body1" sx={{ fontWeight: 500 }}>
                Do you still want to proceed?
              </Typography>
            </>
          )}

          {deleteConfirmStep === 3 && (
            <>
              <Alert 
                severity="error" 
                sx={{ 
                  mb: 3,
                  borderRadius: 2,
                  border: `1px solid ${alpha(theme.palette.error.main, 0.3)}`,
                  background: alpha(theme.palette.error.main, 0.05)
                }}
              >
                <Typography variant="body2" sx={{ fontWeight: 500, color: 'text.primary' }}>
                  FINAL WARNING: This action is irreversible!
                </Typography>
              </Alert>
              <Typography sx={{ mb: 3, fontSize: '1.1rem', fontWeight: 500 }}>
                There is no going back. Confirm deletion.
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                To complete the deletion, type the exact company name below:
              </Typography>
              <TextField
                fullWidth
                label={`Type company name "${company?.name}"`}
                value={confirmationText}
                onChange={(e) => setConfirmationText(e.target.value)}
                sx={{ 
                  mb: 2,
                  '& .MuiInputLabel-root': { fontWeight: 500 },
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    background: alpha(theme.palette.grey[50], 0.8),
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: alpha(theme.palette.primary.main, 0.5)
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderColor: 'primary.main',
                      borderWidth: '2px'
                    }
                  }
                }}
                helperText={`You must type exactly: ${company?.name}`}
                autoFocus
              />
            </>
          )}

          {/* Error display */}
          {error && (
            <Alert 
              severity="error" 
              sx={{ 
                mt: 2,
                borderRadius: 2,
                border: `1px solid ${alpha(theme.palette.error.main, 0.3)}`,
                background: alpha(theme.palette.error.main, 0.05)
              }}
            >
              {error}
            </Alert>
          )}
        </DialogContent>
        <DialogActions sx={{ 
          p: 3, 
          borderTop: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
          background: alpha(theme.palette.grey[50], 0.5),
          gap: 2
        }}>
          <Button 
            onClick={handleDeleteDialogClose}
            disabled={processing}
            sx={{ 
              fontWeight: 500,
              borderRadius: 2,
              px: 3,
              py: 1.2,
              color: 'text.secondary',
              '&:hover': {
                background: alpha(theme.palette.grey[400], 0.1)
              }
            }}
          >
            Cancel
          </Button>
          
          {deleteConfirmStep < 3 && (
            <Button 
              onClick={handleDeleteConfirmStep}
              color="error"
              variant="outlined"
              disabled={processing}
              sx={{
                fontWeight: 500,
                borderRadius: 2,
                px: 4,
                py: 1.2,
                borderColor: 'error.main',
                '&:hover': {
                  transform: 'translateY(-1px)',
                  boxShadow: `0 4px 16px ${alpha(theme.palette.error.main, 0.3)}`
                }
              }}
            >
              {deleteConfirmStep === 1 ? 'Yes, I want to delete' : 'Confirm'}
            </Button>
          )}

          {deleteConfirmStep === 3 && (
            <Button 
              onClick={handleDeleteConfirmStep}
              color="error"
              variant="contained"
              disabled={processing || confirmationText !== company?.name}
              startIcon={processing ? <CircularProgress size={16} /> : <DeleteIcon />}
              sx={{
                fontWeight: 500,
                borderRadius: 2,
                px: 4,
                py: 1.2,
                boxShadow: `0 4px 16px ${alpha(theme.palette.error.main, 0.3)}`,
                '&:hover': {
                  transform: 'translateY(-1px)',
                  boxShadow: `0 6px 20px ${alpha(theme.palette.error.main, 0.4)}`
                }
              }}
            >
              {processing ? 'Deleting...' : 'Delete Permanently'}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
}